import { Box, Button, Center, Heading, HStack, Tag, TagLabel, Text, VStack } from '@chakra-ui/react'
import { faCircle, faVideo } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { DateTime, type Duration } from 'luxon'
import { type ReactElement, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { millisecondsToHumanReadableString } from '@/utils/time'

import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import SessionRegistrationQ from '@/queries/SessionRegistrationQ'
import SessionSimulationQ from '@/queries/SessionSimulationQ'
import TopicQ from '@/queries/TopicQ'
import { HasuraContextWrapper } from '@/client/context/hasura'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import useOneTimeAcknowledgement from '@/client/hooks/useOneTimeAcknowledgement'
import { type Query, SQL } from '@/client/query'
import { getExperimentArm } from '@/client/services/experiments/session-simulator'
import getSessionTimespanString from '@/client/services/series/getSessionTimespanString'

import SessionJoinModal from '@/components/Session/SessionJoinModal/SessionJoinModal'
import getZoomSpeakingMinutesSummary from '@/components/Session/SessionMetrics/getZoomSpeakingMinutesSummary'
import Alert from '@/components/UI/Alert'
import Tippy from '@/components/UI/Tippy'

import Background from './internal/Background'
import { BANNER_STATE_TYPE_TO_OPTIONS } from './internal/bannerStateTypeConfigurations'
import {
  type BannerOptionsButton,
  type BannerOptionsColors,
  type BannerOptionsNextToTitleTag,
  BannerStateType,
  type UseBannerStateProps,
} from './internal/types'
import useBannerState from './internal/useBannerState'

// TODO: Replace SessionJoinBanner with this eventually

function NextToTitleTag({ nextToTitleTag }: { nextToTitleTag: BannerOptionsNextToTitleTag }) {
  return nextToTitleTag != null ? (
    <Tag
      backgroundColor={nextToTitleTag.bgColor}
      color={nextToTitleTag.textColor}
      fontSize="xs"
      fontWeight="semibold"
      px="8px"
      py="4px"
    >
      {nextToTitleTag.icon}
      <TagLabel marginLeft="4px" width={nextToTitleTag.width ?? undefined}>
        {nextToTitleTag.text}
      </TagLabel>
    </Tag>
  ) : null
}

function CornerCountdown({
  showCornerCountdownTag,
  colors,
  startFromNow,
}: {
  showCornerCountdownTag: boolean
  colors: BannerOptionsColors
  startFromNow: Duration
}) {
  return showCornerCountdownTag ? (
    <Box flexShrink="0px">
      <Tag
        backgroundColor="bg"
        border={`2px solid ${chakraTokenToVar(colors.borderIconColor)}`}
        color={colors.accentColor}
        fontSize="sm"
        fontWeight="semibold"
        px="10px"
        py="6px"
        boxShadow={`6px 6px 0px 0px ${chakraTokenToVar(colors.accentColor)}`}
        flexShrink="0px"
      >
        In{' '}
        {millisecondsToHumanReadableString(startFromNow.as('milliseconds'), {
          omitSecondary: true,
        })}
      </Tag>
    </Box>
  ) : null
}

function JoinButton({
  button,
  onButtonClick,
  isUserTutor,
}: {
  button: BannerOptionsButton
  onButtonClick: () => void
  isUserTutor: boolean
}) {
  return button != null ? (
    <Box bg="bg" borderRadius="8px">
      <Tippy content={button.tooltip} disabled={button.tooltip == null}>
        <Button
          variant="outline"
          minW={{ base: '100%', md: 'unset' }}
          px="24px"
          isDisabled={button.disabled}
          onClick={onButtonClick}
          color={isUserTutor ? 'secondary.fg' : 'primary.fg'}
        >
          {isUserTutor ? 'Start' : 'Join'}
        </Button>
      </Tippy>
    </Box>
  ) : null
}

function SeriesLiveBannerInternal({
  series,
  isUserTutor,
  sessionTitle = null,
  sessionTopic,
  onButtonClick,
  hasCompletedSimulation,
  showSimulatorNudge,
  ...props
}: UseBannerStateProps & {
  series: Query
  isUserTutor: boolean
  sessionId: number
  sessionTitle: string | null
  sessionTopic: string
  onButtonClick: () => void
  hasCompletedSimulation: boolean
  showSimulatorNudge: boolean
}): ReactElement | null {
  const seriesId = series(SeriesQ.id) ?? 0
  const { startDateTime: startDate, endDateTime: endDate } = props
  const { type, startFromNow, now } = useBannerState({ ...props })

  if (type === BannerStateType.None) {
    return null
  }

  const bannerOptions = BANNER_STATE_TYPE_TO_OPTIONS[type]({
    startFromNow,
    isUserTutor,
    startDate,
    seriesId,
    hasCompletedSimulation,
    showSimulatorNudge,
    now,
  })
  const { colors, nextToTitleTag, button, alert } = bannerOptions

  const title = sessionTitle ?? sessionTopic
  const subtitle = sessionTitle != null ? sessionTopic : null

  // If we haven't gotten the proper sessionId yet, we disable the button
  if (props.sessionId === 0 && bannerOptions.button != null) {
    bannerOptions.button.disabled = true
  }

  return (
    <VStack
      width="100%"
      spacing="16px"
      borderRadius="8px"
      border={`2px solid ${chakraTokenToVar(colors.borderIconColor)}`}
      backgroundColor={colors.backgroundColor}
      p="24px"
      style={{
        boxShadow: `8px 8px 0px ${chakraTokenToVar(colors.shadowColor)}`,
        overflow: 'hidden',
      }}
      position="relative"
      alignItems="stretch"
    >
      <Background color={colors.shapeColor} />
      <VStack spacing="16px" alignItems="stretch" zIndex={1}>
        <HStack spacing="8px" alignItems="stretch">
          <VStack
            spacing="0px"
            justifyContent="center"
            alignItems="stretch"
            flexGrow="1"
            pl={{ base: 0, md: bannerOptions.showVideoIcon ? '40px' : undefined }}
          >
            <HStack
              alignItems="center"
              justifyContent="space-between"
              mb="12px"
              display={{ base: 'flex', md: 'none' }}
            >
              <FontAwesomeIcon
                mask={faCircle}
                icon={faVideo}
                color={chakraTokenToVar(colors.borderIconColor)}
                transform="shrink-9"
                size="2x"
                width="24px"
              />
              <NextToTitleTag nextToTitleTag={nextToTitleTag} />
              <CornerCountdown
                showCornerCountdownTag={bannerOptions.showCornerCountdownTag}
                colors={colors}
                startFromNow={startFromNow}
              />
            </HStack>
            <HStack spacing="8px" position="relative">
              {bannerOptions.showVideoIcon ? (
                <Box
                  position="absolute"
                  left="-40px"
                  height="32px"
                  mx="auto"
                  display={{ base: 'none', md: 'flex' }}
                >
                  <FontAwesomeIcon
                    mask={faCircle}
                    icon={faVideo}
                    color={chakraTokenToVar(colors.borderIconColor)}
                    transform="shrink-9"
                    size="2x"
                  />
                </Box>
              ) : null}
              <Heading size="xl" color={colors.primaryTextColor}>
                {title}
              </Heading>
              <Box display={{ base: 'none', md: 'flex' }}>
                <NextToTitleTag nextToTitleTag={nextToTitleTag} />
              </Box>
            </HStack>
            {subtitle != null ? <Text color={colors.secondaryTextColor}>{subtitle}</Text> : null}
            {bannerOptions.showTimespanUnderneathTitle ? (
              <Text
                color={colors.secondaryTextColor}
                fontWeight="semibold"
                textTransform="uppercase"
                suppressHydrationWarning
              >
                {getSessionTimespanString({ startDate, endDate, dateFormat: 'day-of-week' })}
              </Text>
            ) : null}
          </VStack>
          <Center display={{ base: 'none', md: 'flex' }}>
            <JoinButton
              button={bannerOptions.button}
              onButtonClick={onButtonClick}
              isUserTutor={isUserTutor}
            />
          </Center>
          <Box display={{ base: 'none', md: 'flex' }}>
            <CornerCountdown
              showCornerCountdownTag={bannerOptions.showCornerCountdownTag}
              colors={colors}
              startFromNow={startFromNow}
            />
          </Box>
        </HStack>
        {bannerOptions.aboveAlert}
        {alert != null ? <Alert variant={alert.variant}>{alert.message}</Alert> : null}
        {bannerOptions.beneathAlert}
        <VStack
          display={{ base: button != null ? 'flex' : 'none', md: 'none' }}
          alignItems="stretch"
        >
          <JoinButton
            button={bannerOptions.button}
            onButtonClick={onButtonClick}
            isUserTutor={isUserTutor}
          />
        </VStack>
      </VStack>
    </VStack>
  )
}

export default function SeriesLiveBanner({ series }: { series: Query }): ReactElement | null {
  const user = useUser()
  const query = useQuery()

  const [showSessionJoinModal, setShowSessionJoinModal] = useState(false)

  const simulatorExperimentArm = getExperimentArm(user.id)

  const nextSession = series.one(
    SQL`${SeriesQ.getSessions({}, SQL`${SessionQ.end_date} > CURRENT_TIMESTAMP`)} LIMIT 1`
  )
  const sessionId = nextSession(SessionQ.id) ?? 0
  const startDateTime = DateTime.fromISO(nextSession(SessionQ.start_date) ?? '')
  const endDateTime = DateTime.fromISO(nextSession(SessionQ.end_date) ?? '')
  const zoomEndDateString = nextSession(SessionQ.zoom_end_date)
  const zoomEndDateTime = zoomEndDateString != null ? DateTime.fromISO(zoomEndDateString) : null
  const attemptedHosting = nextSession(SessionQ.attempted_hosting) ?? false
  const isUserTutor = series(SeriesQ.isHost({ userId: user.id })) ?? false
  const isUserRegistered = series(SeriesQ.isUserRegistered(user.id)) ?? false
  const isParticipant = isUserRegistered || isUserTutor
  const sessionTitle = nextSession(SessionQ.title)
  const viewpointSettings = nextSession(SessionQ.getViewpointSettings())
  const sessionTopic = nextSession.one(SessionQ.getTopic())
  const sessionTopicName = sessionTopic(TopicQ.name) ?? ''
  const seriesId = series(SeriesQ.id) ?? 0

  const sessionRegistration = nextSession.one(
    SessionRegistrationQ.where({ sessionId: SessionQ.id, userId: user.id })
  )
  const sessionRegistrationId = sessionRegistration(SessionRegistrationQ.id)
  const hasSubmittedViewpoint = sessionRegistration(SessionRegistrationQ.getViewpoint()) != null

  const {
    acknowledgedAt: recordingPolicyAcknowledgedAt,
    onAcknowledgement: onAcknowledgeRecordingPolicy,
  } = useOneTimeAcknowledgement('accepted-recording-policy-02-22-23')

  const isSATSeries = series(SeriesQ.isSATSeries()) ?? false

  const mostRecentlyAttendedSession = series.one(SeriesQ.getMostRecentlyAttendedSession(user.id))
  const { tutorSpeakingPercentage } = getZoomSpeakingMinutesSummary(mostRecentlyAttendedSession)

  const hasCompletedSimulation =
    query<boolean>(
      SQL`EXISTS(
      SELECT 1 ${SessionSimulationQ.where({ userId: user.id })}
    )`
    ) ?? false

  const showSimulatorNudge =
    isUserTutor && simulatorExperimentArm === 'experiment' && !hasCompletedSimulation

  if (!isParticipant) {
    return null
  }

  return (
    <>
      <HasuraContextWrapper LoadingComponent={() => null}>
        <SeriesLiveBannerInternal
          series={series}
          sessionId={sessionId}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          zoomEndDateTime={zoomEndDateTime}
          attemptedHosting={attemptedHosting}
          isUserTutor={isUserTutor}
          sessionTitle={sessionTitle}
          sessionTopic={sessionTopicName}
          onButtonClick={() => setShowSessionJoinModal(true)}
          hasCompletedSimulation={hasCompletedSimulation}
          showSimulatorNudge={showSimulatorNudge}
        />
      </HasuraContextWrapper>
      <SessionJoinModal
        sessionId={sessionId}
        seriesId={seriesId}
        sessionRegistrationId={sessionRegistrationId}
        viewpointSettings={viewpointSettings}
        topicName={sessionTopicName}
        isHost={isUserTutor}
        open={showSessionJoinModal}
        onClose={() => setShowSessionJoinModal(false)}
        recordingPolicyAcknowledgedAt={recordingPolicyAcknowledgedAt}
        onAcknowledgeRecordingPolicy={onAcknowledgeRecordingPolicy}
        pastSessionTutorSpeakingPercentage={tutorSpeakingPercentage}
        showSATBootcampLearnerFeedback={isSATSeries}
        hasSubmittedViewpoint={hasSubmittedViewpoint}
      />
    </>
  )
}
